import { useCallback, useState } from 'react';
import { ColumnApi } from 'ag-grid-community';
import { RowGroupColumn } from 'customHooks/useRowGroupColumns';

const updateRowGroupColumnState = (colApi: ColumnApi | null, selectedGroups: RowGroupColumn[]): void => {
  if (!colApi) return;

  const rowGroupColumnState = selectedGroups.map(({ colId, isSelected, groupOrder }) => {
    return {
      colId,
      rowGroup: isSelected,
      rowGroupIndex: groupOrder,
    };
  });

  colApi.applyColumnState({
    state: rowGroupColumnState,
    applyOrder: true,
  });
};

export interface UseReportRowGroupsReturn {
  selectedRowGroupColumns: RowGroupColumn[];
  handleToggleRowGroupColumn: (selectedGroups: RowGroupColumn[]) => void;
  resetRowGroupColumns: () => void;
  updateRowGroupColumnState: (colApi: ColumnApi | null, selectedGroups: RowGroupColumn[]) => void;
}

export const useReportRowGroups = (
  rowGroupColumns: RowGroupColumn[],
  columnApi: ColumnApi | null,
): UseReportRowGroupsReturn => {
  const [selectedRowGroupColumns, setSelectedRowGroupColumns] = useState<RowGroupColumn[]>(rowGroupColumns);

  const handleToggleRowGroupColumn = useCallback(
    (selectedGroups: RowGroupColumn[]): void => {
      const selectedRowGroupColumnsIds = selectedGroups.map(({ colId }) => colId);

      const updatedRowGroupColumns = rowGroupColumns.map((rowGroup) => {
        const isSelected = selectedRowGroupColumnsIds.includes(rowGroup.colId);

        return {
          ...rowGroup,
          isSelected,
          groupOrder: isSelected ? selectedRowGroupColumnsIds.indexOf(rowGroup.colId) : undefined,
        };
      });

      setSelectedRowGroupColumns(updatedRowGroupColumns);
      updateRowGroupColumnState(columnApi, updatedRowGroupColumns);
    },
    [rowGroupColumns, columnApi],
  );

  const resetRowGroupColumns = useCallback((): void => {
    setSelectedRowGroupColumns(rowGroupColumns);
    updateRowGroupColumnState(columnApi, rowGroupColumns);
  }, [rowGroupColumns, columnApi]);

  return {
    selectedRowGroupColumns,
    handleToggleRowGroupColumn,
    resetRowGroupColumns,
    updateRowGroupColumnState,
  };
};
